import React, { useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight, User, UserIcon } from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: false,
      offset: 100,
    });

    // Fetch testimonials from API
    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/testimonials/all-testimonials`);
        setTestimonials(res.data || []);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();

    return () => {
      AOS.refresh();
    };
  }, []);

  const itemsPerSlide = 2;
  const totalSlides = Math.ceil(testimonials.length / itemsPerSlide);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-3 h-3 xs:w-4 xs:h-4 ${
          i < rating ? "text-orange-400 fill-orange-400" : "text-gray-300"
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading testimonials...</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-white overflow-hidden">
      {/* Background Image Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{
          backgroundImage:
            'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800"><rect fill="%23374151" width="1200" height="800"/><g fill="%23ffffff" opacity="0.1"><rect x="100" y="100" width="200" height="150"/><rect x="350" y="200" width="150" height="100"/><rect x="600" y="150" width="180" height="120"/><rect x="850" y="100" width="200" height="180"/><rect x="200" y="350" width="160" height="200"/><rect x="450" y="400" width="180" height="150"/><rect x="700" y="450" width="200" height="160"/><rect x="950" y="350" width="150" height="200"/></g></svg>\')',
        }}
      />

      <div className="relative z-10 px-2 xs:px-3 sm:px-4 md:px-6 lg:px-12 py-8 xs:py-10 sm:py-12 md:py-16 lg:py-24">
        <div className="w-full mx-auto">
          {/* Header */}
          <div
            className="mb-8 xs:mb-10 sm:mb-12 lg:mb-10"
            data-aos="fade-right"
          >
            <div className="flex items-center mb-3 xs:mb-4">
              <div
                className="border-l-2 border-[#FF5A3C] pl-3"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                <span className="text-[#FF5A3C] text-sm sm:text-base font-medium">
                  Testimonials
                </span>
              </div>
            </div>
            <h2 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              See What Our Client
              <br />
              Says About Us
            </h2>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-end mb-6 xs:mb-7 sm:mb-8 space-x-2 xs:space-x-3 sm:space-x-4">
            <button
              onClick={prevSlide}
              disabled={totalSlides <= 1}
              className="w-10 h-10 xs:w-11 xs:h-11 sm:w-12 sm:h-12 rounded-full bg-gray-200 hover:bg-gray-300 transition-all duration-300 flex items-center justify-center disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5 xs:w-6 xs:h-6 text-gray-700" />
            </button>
            <button
              onClick={nextSlide}
              disabled={totalSlides <= 1}
              className="w-10 h-10 xs:w-11 xs:h-11 sm:w-12 sm:h-12 rounded-full bg-[#FF5A3C] hover:bg-orange-600 transition-all duration-300 flex items-center justify-center disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5 xs:w-6 xs:h-6 text-white" />
            </button>
          </div>

          {/* Testimonials Container */}
          <div className="overflow-hidden" data-aos="zoom-in">
            {testimonials.length === 0 ? (
              <p className="text-center text-gray-500">
                No testimonials available.
              </p>
            ) : (
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${
                    currentIndex * (100 / totalSlides)
                  }%)`,
                  width: `${totalSlides * 100}%`,
                }}
              >
                {Array.from({ length: totalSlides }, (_, slideIndex) => (
                  <div
                    key={slideIndex}
                    className="w-full flex-shrink-0"
                    style={{ width: `${100 / totalSlides}%` }}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 xs:gap-5 sm:gap-6 lg:gap-8 px-1 xs:px-2">
                      {testimonials
                        .slice(
                          slideIndex * itemsPerSlide,
                          slideIndex * itemsPerSlide + itemsPerSlide
                        )
                        .map((testimonial) => (
                          <div
                            key={testimonial._id}
                            className="bg-white rounded-xl xs:rounded-2xl p-4 xs:p-5 sm:p-6 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                          >
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4 xs:mb-5 sm:mb-6">
                              <div className="flex items-center">
                                <div className="w-10 h-10 xs:w-12 xs:h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden mr-2 xs:mr-3 sm:mr-4 flex-shrink-0">
                                  {testimonial.image ? (
                                    <img
                                    src={testimonial.avatarUrl}
                                    alt={testimonial.name}
                                    className="w-full h-full object-cover"
                                  />
                                  ) : (
                                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                      <UserIcon className="w-8 h-8 text-gray-500" />
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <h3 className="text-sm xs:text-base sm:text-lg font-semibold text-gray-900 mb-1">
                                    {testimonial.name}
                                  </h3>
                                  <p className="text-xs xs:text-sm text-gray-500 font-medium tracking-wider">
                                    {testimonial.type}
                                  </p>
                                </div>
                              </div>

                              {/* Rating */}
                              <div className="flex">
                                {renderStars(testimonial.rating || 0)}
                              </div>
                            </div>

                            {/* Message */}
                            <p className="text-gray-600 leading-relaxed text-xs xs:text-sm sm:text-base">
                              "{testimonial.message}"
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dots */}
          {totalSlides > 1 && (
            <div className="flex justify-center mt-6 xs:mt-7 sm:mt-8 space-x-1 xs:space-x-2">
              {Array.from({ length: totalSlides }, (_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? "bg-orange-500 w-6 xs:w-8"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSection;



// import React, { useState } from "react";
// import { Star, ChevronLeft, ChevronRight } from "lucide-react";
// import AOS from "aos";
// import "aos/dist/aos.css";
// import { useEffect } from "react";

// const TestimonialsSection = () => {
//   const [currentIndex, setCurrentIndex] = useState(0);

//   useEffect(() => {
//     // Initialize AOS with once: true to prevent repeated animations
//     AOS.init({
//       duration: 800,
//       easing: "ease-in-out",
//       once: false, // Animation happens only once
//       offset: 100, // Trigger animation 100px before element comes into view
//       delay: 0,
//     });

//     // Cleanup function to refresh AOS on component unmount
//     return () => {
//       AOS.refresh();
//     };
//   }, []);

//   const testimonials = [
//     {
//       id: 1,
//       name: "Dixita Warner",
//       role: "FIRST-TIME HOMEBUYER",
//       image:
//         "https://media.istockphoto.com/id/1682296067/photo/happy-studio-portrait-or-professional-man-real-estate-agent-or-asian-businessman-smile-for.jpg?s=612x612&w=0&k=20&c=9zbG2-9fl741fbTWw5fNgcEEe4ll-JegrGlQQ6m54rg=",
//       rating: 4,
//       text: "Buying Our First Home Was A Dream Come True, And The Team Made It Seamless. From Site Visits To Loan Support And Final Registration, Everything Was Handled With Care And Professionalism. Highly Recommended!",
//     },
//     {
//       id: 2,
//       name: "Harry Porter",
//       role: "REAL ESTATE INVESTOR",
//       image:
//         "https://media.istockphoto.com/id/1682296067/photo/happy-studio-portrait-or-professional-man-real-estate-agent-or-asian-businessman-smile-for.jpg?s=612x612&w=0&k=20&c=9zbG2-9fl741fbTWw5fNgcEEe4ll-JegrGlQQ6m54rg=",
//       rating: 4,
//       text: "I've Invested In Multiple Projects Over The Years, But This Team Stands Out For Its Transparency And Timely Delivery. The Location Is Prime, And I've Already Seen Great Appreciation In Value.",
//     },
//     {
//       id: 3,
//       name: "John Smith",
//       role: "PROPERTY DEVELOPER",
//       image:
//         "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQkAJEkJQ1WumU0hXNpXdgBt9NUKc0QDVIiaw&s",
//       rating: 5,
//       text: "Working with this team has been exceptional. Their attention to detail and commitment to quality is unmatched in the industry. Every project delivered on time and beyond expectations.",
//     },
//     {
//       id: 4,
//       name: "Sarah Johnson",
//       role: "COMMERCIAL INVESTOR",
//       image:
//         "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQkAJEkJQ1WumU0hXNpXdgBt9NUKc0QDVIiaw&s",
//       rating: 4,
//       text: "The commercial properties we've invested in through this team have shown excellent returns. Their market knowledge and strategic approach make them our go-to partners.",
//     },
//   ];

//   const itemsPerSlide = 2;
//   const totalSlides = Math.ceil(testimonials.length / itemsPerSlide);

//   const nextSlide = () => {
//     setCurrentIndex((prev) => (prev + 1) % totalSlides);
//   };

//   const prevSlide = () => {
//     setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
//   };

//   const renderStars = (rating) => {
//     return Array.from({ length: 5 }, (_, i) => (
//       <Star
//         key={i}
//         className={`w-3 h-3 xs:w-4 xs:h-4 ${
//           i < rating ? "text-orange-400 fill-orange-400" : "text-gray-300"
//         }`}
//       />
//     ));
//   };

//   return (
//     <div className="relative min-h-screen bg-white overflow-hidden">
//       {/* Background Image Overlay */}
//       <div
//         className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
//         style={{
//           backgroundImage:
//             'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800"><rect fill="%23374151" width="1200" height="800"/><g fill="%23ffffff" opacity="0.1"><rect x="100" y="100" width="200" height="150"/><rect x="350" y="200" width="150" height="100"/><rect x="600" y="150" width="180" height="120"/><rect x="850" y="100" width="200" height="180"/><rect x="200" y="350" width="160" height="200"/><rect x="450" y="400" width="180" height="150"/><rect x="700" y="450" width="200" height="160"/><rect x="950" y="350" width="150" height="200"/></g></svg>\')',
//         }}
//       />

//       <div className="relative z-10 px-2 xs:px-3 sm:px-4 md:px-6 lg:px-12 py-8 xs:py-10 sm:py-12 md:py-16 lg:py-24">
//         <div className="w-full mx-auto">
//           {/* Header */}
//           <div
//             className="mb-8 xs:mb-10 sm:mb-12 lg:mb-10"
//             data-aos="fade-right"
//           >
//             <div className="flex items-center mb-3 xs:mb-4">
//               <div className="border-l-2 border-[#FF5A3C] pl-3" data-aos="fade-up" data-aos-delay="300">
//                 <span className="text-[#FF5A3C] text-sm sm:text-base font-medium">
//                   Testimonials
//                 </span>
//               </div>
//             </div>
//             <h2 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
//               See What Our Client
//               <br />
//               Says About Us
//             </h2>
//           </div>

//           {/* Navigation Buttons */}
//           <div className="flex justify-end mb-6 xs:mb-7 sm:mb-8 space-x-2 xs:space-x-3 sm:space-x-4">
//             <button
//               onClick={prevSlide}
//               className="w-10 h-10 xs:w-11 xs:h-11 sm:w-12 sm:h-12 rounded-full bg-gray-200 hover:bg-gray-300 transition-all duration-300 flex items-center justify-center"
//             >
//               <ChevronLeft className="w-5 h-5 xs:w-6 xs:h-6 text-gray-700" />
//             </button>
//             <button
//               onClick={nextSlide}
//               className="w-10 h-10 xs:w-11 xs:h-11 sm:w-12 sm:h-12 rounded-full bg-[#FF5A3C] hover:bg-orange-600 transition-all duration-300 flex items-center justify-center"
//             >
//               <ChevronRight className="w-5 h-5 xs:w-6 xs:h-6 text-white" />
//             </button>
//           </div>

//           {/* Testimonials Container */}
//           <div className="overflow-hidden" data-aos="zoom-in">
//             <div
//               className="flex transition-transform duration-500 ease-in-out"
//               style={{
//                 transform: `translateX(-${
//                   currentIndex * (100 / totalSlides)
//                 }%)`,
//                 width: `${totalSlides * 100}%`,
//               }}
//             >
//               {Array.from({ length: totalSlides }, (_, slideIndex) => (
//                 <div
//                   key={slideIndex}
//                   className="w-full flex-shrink-0"
//                   style={{ width: `${100 / totalSlides}%` }}
//                 >
//                   <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 xs:gap-5 sm:gap-6 lg:gap-8 px-1 xs:px-2">
//                     {testimonials
//                       .slice(
//                         slideIndex * itemsPerSlide,
//                         slideIndex * itemsPerSlide + itemsPerSlide
//                       )
//                       .map((testimonial) => (
//                         <div
//                           key={testimonial.id}
//                           className="bg-white rounded-xl xs:rounded-2xl p-4 xs:p-5 sm:p-6 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
//                         >
//                           {/* Header with Avatar and Info */}
//                           <div className="flex items-start justify-between mb-4 xs:mb-5 sm:mb-6">
//                             <div className="flex items-center">
//                               <div className="w-10 h-10 xs:w-12 xs:h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden mr-2 xs:mr-3 sm:mr-4 flex-shrink-0">
//                                 <img
//                                   src={testimonial?.image}
//                                   alt={testimonial.name}
//                                   className="w-full h-full object-cover"
//                                 />
//                               </div>
//                               <div>
//                                 <h3 className="text-sm xs:text-base sm:text-lg font-semibold text-gray-900 mb-1">
//                                   {testimonial.name}
//                                 </h3>
//                                 <p className="text-xs xs:text-sm text-gray-500 font-medium tracking-wider">
//                                   {testimonial.role}
//                                 </p>
//                               </div>
//                             </div>

//                             {/* Rating Stars */}
//                             <div className="flex">
//                               {renderStars(testimonial.rating)}
//                             </div>
//                           </div>

//                           {/* Testimonial Text */}
//                           <p className="text-gray-600 leading-relaxed text-xs xs:text-sm sm:text-base">
//                             "{testimonial.text}"
//                           </p>
//                         </div>
//                       ))}
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* Dots Indicator */}
//           <div className="flex justify-center mt-6 xs:mt-7 sm:mt-8 space-x-1 xs:space-x-2">
//             {Array.from({ length: totalSlides }, (_, index) => (
//               <button
//                 key={index}
//                 onClick={() => setCurrentIndex(index)}
//                 className={`w-2 h-2 rounded-full transition-all duration-300 ${
//                   index === currentIndex
//                     ? "bg-orange-500 w-6 xs:w-8"
//                     : "bg-gray-300"
//                 }`}
//               />
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default TestimonialsSection;